/**
 * 手元の木と、未確定の操作。
 *
 * ## 楽観適用と再送を 1 箇所に閉じる
 * 打った操作は**先に手元へ当てて**から送る。確定は連番付きで返ってくる。切れている間の操作は
 * 溜めておき、繋がったらそのまま送り直す——操作 id が同じなので二重に適用されることはない
 * （Durable Object 側が `op_id` の一意制約で弾く）。この「溜めて送り直す」があるかどうかが、
 * 電波の切れた場所で書いた内容が消えるかどうかを分ける。
 */

import { applyOp, MemoryNodeStore, type NodeSnapshot, type SyncEnvelope, type SyncOp } from "@biside/domain";

export class OutlineStore {
  readonly tree = new MemoryNodeStore();
  /** サーバが確定させた最後の連番。再接続時にここから続きをもらう。 */
  seq = 0;
  /** まだ確定していない操作。順番に意味があるので配列で持つ。 */
  private pending: SyncOp[] = [];
  private listeners = new Set<() => void>();

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  /** 初期表示。スナップショットで置き換える。 */
  reset(nodes: NodeSnapshot[], seq: number): void {
    this.tree.nodes.clear();
    for (const n of nodes) this.tree.put(n);
    this.seq = seq;
    this.notify();
  }

  /** 自分の操作。手元へ当ててから、送るべき操作列を返す。 */
  local(ops: SyncOp[]): SyncOp[] {
    if (ops.length === 0) return [];
    const at = new Date().toISOString();
    for (const op of ops) {
      applyOp(this.tree, op, at);
      this.pending.push(op);
    }
    this.notify();
    return ops;
  }

  /** サーバから届いた確定。自分が出したものも含めて同じ道を通す。 */
  remote(envelopes: SyncEnvelope[]): void {
    if (envelopes.length === 0) return;
    for (const env of envelopes) {
      applyOp(this.tree, env.op, env.at);
      this.seq = Math.max(this.seq, env.seq);
      // 確定したものは未確定の列から外す。
      const i = this.pending.findIndex((p) => p.opId === env.op.opId);
      if (i >= 0) this.pending.splice(i, 1);
    }
    this.notify();
  }

  /** 再接続したときに送り直すもの。 */
  unsent(): SyncOp[] {
    return [...this.pending];
  }
  get pendingCount(): number {
    return this.pending.length;
  }
}

/** 画面に出す行の並び。折りたたまれた枝は畳んだまま返す。 */
export interface VisibleRow {
  node: NodeSnapshot;
  depth: number;
  hasChildren: boolean;
}

export function visibleRows(tree: MemoryNodeStore, parentId: string | null = null, depth = 0): VisibleRow[] {
  const out: VisibleRow[] = [];
  for (const node of tree.children(parentId)) {
    const kids = tree.children(node.id);
    out.push({ node, depth, hasChildren: kids.length > 0 });
    if (!node.collapsed && kids.length > 0) out.push(...visibleRows(tree, node.id, depth + 1));
  }
  return out;
}
